import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { DEFAULT_PAGE_SIZE, NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP } from '../const';
import { RelewiseCategoryService } from '../services/CategoryService';
import { ProductListSortOption, parseProductListSortOption } from '../../../utils/productListSort';
import { CategoryProductsResponse } from '../types/mapped';
import { createEmptyProductListResponse } from '../utils';
import { FilterOptions, parseFilterOptions } from '../../../utils/productListFilters';

const FIRST_PAGE = 1;
const DECIMAL_RADIX = 10;
type ProductListConfig = { itemsPerPage: number | undefined; variantsPerProduct: number | undefined };

const getCategoryRecordId = (inputData: ICreateActionContext<ProductListConfig>): string | undefined => {
    return inputData.requestContext.urlTokens.recordId;
};

export class GetCategoryProductsDataInput implements IActionInput {
    public categoryRecordId: string | undefined;
    public itemsPerPage: number;
    public variantsPerProduct: number;
    public page: number;
    public sort: ProductListSortOption;
    public filterOptions?: FilterOptions;

    public constructor(
        categoryRecordId: string | undefined,
        itemsPerPage: number,
        variantsPerProduct: number,
        page: number,
        sort: ProductListSortOption,
        filterOptions?: FilterOptions
    ) {
        this.categoryRecordId = categoryRecordId;
        this.itemsPerPage = itemsPerPage;
        this.variantsPerProduct = variantsPerProduct;
        this.page = page;
        this.sort = sort;
        this.filterOptions = filterOptions;
    }

    public getCacheKey = () =>
        `RelewiseCategoryProducts-${this.categoryRecordId ?? 'none'}-${this.itemsPerPage}-${this.page}-${this.sort}-${JSON.stringify(
            this.filterOptions ?? {}
        )}`;

    public getCacheObjectType = () => 'RelewiseCategoryProducts';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext<ProductListConfig>) => {
    const categoryRecordId = getCategoryRecordId(inputData);
    const itemsPerPage = inputData.config?.itemsPerPage ?? DEFAULT_PAGE_SIZE;
    const variantsPerProduct = inputData.config?.variantsPerProduct ?? NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP;

    const query = inputData.requestContext.query ?? {};
    const rawPage = parseInt(query.page ?? String(FIRST_PAGE), DECIMAL_RADIX);
    const page = isNaN(rawPage) || rawPage < FIRST_PAGE ? FIRST_PAGE : rawPage;
    const sort = parseProductListSortOption(query.sort);

    const filterOptions = parseFilterOptions(k => query[k]);

    return new GetCategoryProductsDataInput(categoryRecordId, itemsPerPage, variantsPerProduct, page, sort, filterOptions);
};

export async function getCategoryProductsData(input: GetCategoryProductsDataInput, ctx: IActionContext): Promise<CategoryProductsResponse> {
    const emptyResponse = createEmptyProductListResponse({ itemsPerPage: input.itemsPerPage, currentPage: input.page });

    if (!input.categoryRecordId) {
        const message = '[getProductListCategoryData] Missing categoryRecordId. Returning empty PLP';
        console.error(message);
        ctx.telemetry.error(message);
        return emptyResponse;
    }

    const categoryService = new RelewiseCategoryService(ctx.requestContext, ctx.telemetry);
    const result = await categoryService.getCategoryProducts(
        input.categoryRecordId,
        input.itemsPerPage,
        input.page,
        input.sort,
        input.variantsPerProduct,
        input.filterOptions
    );
    return result ?? emptyResponse;
}

export const getCategoryProductsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/custom/get-category-products',
    action: <IAction<CategoryProductsResponse>>getCategoryProductsData,
    input: createInput
});

export default getCategoryProductsDataAction;
